import { useEffect } from "react";
import { createPortal } from "react-dom";
import { SlidersHorizontal } from "lucide-react";
import { useIdioma } from "../i18n/IdiomaProvider";
import { BotaoContorno } from "../components/ui/BotaoAcao";
import { BotaoTopoPrimario } from "../components/ui/BotoesTopo";
import { CabecalhoModal, RodapeModal } from "../components/ui/CascaModal";
import { usePeriodoInterativo } from "./usePeriodoInterativo";
import { PainelPeriodo } from "./PainelPeriodo";
import styles from "./FiltroPeriodoModal.module.css";

interface FiltroPeriodoModalProps {
  aberto: boolean;
  inicio?: string;
  fim?: string;
  onFechar: () => void;
  onAplicar: (inicio: string, fim: string) => void;
  onLimpar?: () => void;
}

export function FiltroPeriodoModal({
  aberto,
  inicio,
  fim,
  onFechar,
  onAplicar,
  onLimpar,
}: FiltroPeriodoModalProps) {
  const { traduzir } = useIdioma();
  const estado = usePeriodoInterativo({ ativo: aberto, inicio, fim });

  useEffect(() => {
    if (!aberto) return;
    const original = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = original;
    };
  }, [aberto]);

  useEffect(() => {
    if (!aberto) return;

    function fecharPorEscape(evento: KeyboardEvent) {
      if (evento.key === "Escape") onFechar();
    }

    window.addEventListener("keydown", fecharPorEscape);
    return () => window.removeEventListener("keydown", fecharPorEscape);
  }, [aberto, onFechar]);

  if (!aberto || typeof document === "undefined") return null;

  function fecharTudo() {
    onFechar();
  }

  function aplicarPeriodo() {
    const resultado = estado.obterResultadoAplicar();
    if (!resultado) return;
    onAplicar(resultado.inicio, resultado.fim);
    onFechar();
  }

  function limparPeriodo() {
    onLimpar?.();
    onFechar();
  }

  return createPortal(
    <div data-modal-overlay="true" className={styles.overlay} onClick={fecharTudo}>
      <div
        data-vidro="true"
        data-modal-surface="true"
        data-tour="period-panel"
        className={styles.modal}
        role="dialog"
        aria-modal="true"
        aria-label={traduzir("Ajustar intervalo temporal")}
        onClick={(evento) => evento.stopPropagation()}
      >
        <CabecalhoModal
          icone={<SlidersHorizontal aria-hidden="true" />}
          titulo="Ajustar Intervalo Temporal"
          subtitulo={estado.resumoPeriodo || undefined}
          onFechar={onFechar}
        />

        <div className={styles.corpoModal}>
          <PainelPeriodo estado={estado} />

          <RodapeModal className={styles.rodapeAlinhado}>
            <BotaoContorno estilo="topo" className={styles.botaoModal} onClick={limparPeriodo}>
              Limpar
            </BotaoContorno>
            <BotaoTopoPrimario
              className={styles.botaoModal}
              onClick={aplicarPeriodo}
              disabled={!estado.podeAplicar}
            >
              Filtrar
            </BotaoTopoPrimario>
          </RodapeModal>
        </div>
      </div>
    </div>,
    document.body
  );
}
